// src/components/Games/VixiaPrime.js
import React, { useState, useEffect, useCallback, useRef } from 'react';

// ============================================
// GAME CONSTANTS
// ============================================

const GRID_WIDTH = 40;
const GRID_HEIGHT = 20;
const FOG_RADIUS = 4;
const MAX_MEMORY_FRAGMENTS = 12;
const MAX_HAZARDS = 12;
const FRAGMENTS_TO_REMEMBER = 7;
const WHISPER_INTERVAL = 7;
const MEMORY_DECAY_INTERVAL = 12;
const TOTAL_DAYS = 3;

const TILE = {
  EMPTY: '.',
  SNOW: '·',
  DEEP_SNOW: '~',
  DRIFT: '≈',
  PLAYER: '@',
  SEBASTIAN: 'S',
  FRAGMENT: '♦',
  CAMP: '▲',
  BODY: '×',
  HAZARD: '░',
  ROCK: '█',
  FOOTPRINT: ',',
  TENT: '⌂',
};

const TILE_COLORS = {
  [TILE.EMPTY]: '#1a1a2a',
  [TILE.SNOW]: '#334455',
  [TILE.DEEP_SNOW]: '#2a3a4a',
  [TILE.DRIFT]: '#3a4a5a',
  [TILE.PLAYER]: '#00e5ff',
  [TILE.SEBASTIAN]: '#ff0040',
  [TILE.FRAGMENT]: '#ffb000',
  [TILE.CAMP]: '#8b00ff',
  [TILE.BODY]: '#553333',
  [TILE.HAZARD]: '#4a4a6a',
  [TILE.ROCK]: '#2a2a3a',
  [TILE.FOOTPRINT]: '#1a2a3a',
  [TILE.TENT]: '#556677',
  'fog': '#000000',
};

const DIRECTIONS = {
  ArrowUp: { dx: 0, dy: -1 },
  ArrowDown: { dx: 0, dy: 1 },
  ArrowLeft: { dx: -1, dy: 0 },
  ArrowRight: { dx: 1, dy: 0 },
  w: { dx: 0, dy: -1 },
  s: { dx: 0, dy: 1 },
  a: { dx: -1, dy: 0 },
  d: { dx: 1, dy: 0 },
  W: { dx: 0, dy: -1 },
  S: { dx: 0, dy: 1 },
  A: { dx: -1, dy: 0 },
  D: { dx: 1, dy: 0 },
};

// ============================================
// STORY CONTENT
// ============================================

const MEMORY_FRAGMENTS_DATA = [
  {
    name: 'ANNE\'S PHOTOGRAPH',
    description: 'A photograph of a young woman with black hair and hazel eyes. A friendly smile. On the back: "Your wife Anne."',
    memory: 'You\'re together in the kitchen of a small apartment. Paint is peeling off the walls. Neon light shines through the windows. Down the hallway, a baby cries. "We\'ll figure it out," you tell her. "We always do."',
    type: 'anne',
  },
  {
    name: 'ANNE\'S KISS',
    description: 'The memory surfaces like something rising from deep water.',
    memory: 'She leans forward and kisses you. Remembering her kiss, you\'re overwhelmed with emotion. You begin to cry. You can\'t remember why you stopped.',
    type: 'anne',
  },
  {
    name: 'ANNE\'S VOICE',
    description: 'A recording device, half-frozen. You press play.',
    memory: '"Come home safe, Amir. Sophia drew you a picture. It\'s a rocket ship. She says it\'s going to bring you back to us." A pause. "I believe her."',
    type: 'anne',
  },
  {
    name: 'SOPHIA\'S NAME',
    description: 'A name, written in your own handwriting on a scrap of paper.',
    memory: 'You\'re in the hospital. Anne is holding a crying baby in her arms. Your daughter. Sarah? Jessica? No — Sophia. Her name is Sophia. You like the name.',
    type: 'sophia',
  },
  {
    name: 'SOPHIA\'S DRAWING',
    description: 'A child\'s drawing, folded into a tiny square. Crayon on paper.',
    memory: 'A rocket ship. Three stick figures inside — a tall one, a medium one, and a tiny one with a big smile. Written in wobbly letters: "DADY COMES HOME." You can\'t breathe.',
    type: 'sophia',
  },
  {
    name: 'SOPHIA\'S BIRTHDAY',
    description: 'A candle stub. Pink wax. The number 3.',
    memory: 'Sophia blows out the candles. Three years old. Anne is laughing. The apartment is small and the cake is lopsided but Sophia is screaming with joy. You remember joy. You remember what it felt like.',
    type: 'sophia',
  },
  {
    name: 'THE CONTRACT',
    description: 'A crumpled document with your signature at the bottom.',
    memory: 'An office. Anne is sitting next to you, crying, as you sign a paper. "It\'s for Sophia," you tell her. "For her future. Don\'t worry, everything will be fine."',
    type: 'truth',
  },
  {
    name: 'FENWAY PARK',
    description: 'The ghost of a summer evening. The crack of a bat.',
    memory: 'The crack of the bat echoed through Fenway Park as you looked up at the evening sun, holding your father\'s hand. Why would this planet speak to you about Boston? What does it know about fathers and sons?',
    type: 'childhood',
  },
  {
    name: 'THE APARTMENT',
    description: 'A set of keys. You don\'t recognize the address on the tag.',
    memory: 'Government housing projects. A small apartment with peeling paint. But it was yours. Yours and Anne\'s and Sophia\'s. You were poor, but you had a happy life together, didn\'t you? Weren\'t you in love?',
    type: 'home',
  },
  {
    name: 'CALEB\'S JOURNAL',
    description: 'A leather journal, half-buried in the snow. The pages are filled with names.',
    memory: 'Eight names, each crossed out. Underneath each name, a vision: an endless tunnel of light; discussions with deceased relatives; the future and past collapsing into a single, instantaneous moment. You are Subject 8.',
    type: 'truth',
  },
  {
    name: 'THE BROTH',
    description: 'An empty bowl. A chemical aftertaste lingers on your tongue.',
    memory: 'The broth tastes good, but there\'s a chemical aftertaste. Sebastian hands you the bowl every morning. You drink it without question. You always drink it without question. Why?',
    type: 'truth',
  },
  {
    name: 'DR. LAMBERT\'S VOICE',
    description: 'Static on the radio. A voice you weren\'t meant to hear.',
    memory: '"Subject 7 has reached an optimal neural state." The words echo in your skull. Subject 7. Caleb was Subject 7. You are Subject 8. This isn\'t an expedition. It\'s an experiment.',
    type: 'truth',
  },
  {
    name: 'CALEB\'S WARNING',
    description: 'Words scratched into a rock with a knife.',
    memory: '"I think Sebastian is lying to us. I don\'t think we\'re exploring. I think they\'re studying the planet. Seeing how much of us it\'s able to take. How much is still left when it\'s done."',
    type: 'truth',
  },
  {
    name: 'THE LULLABY',
    description: 'A melody you can\'t place. It loops in your head.',
    memory: 'You\'re rocking Sophia to sleep. You\'re singing something — a song your mother sang to you. Anne is watching from the doorway. She\'s smiling. You can\'t remember the words anymore, but you remember the feeling.',
    type: 'sophia',
  },
  {
    name: 'THE RECIPROCATING SAW',
    description: 'A tool, cleaned but not cleaned well enough. Rust-colored stains on the teeth.',
    memory: 'You find it in Sebastian\'s pack while he sleeps. A reciprocating saw. Medical grade. Why would an expedition guide carry a surgical saw? Your hands shake. You put it back.',
    type: 'truth',
  },
  {
    name: 'ANNE\'S LAST LETTER',
    description: 'A letter, sealed. Your name on the front in her handwriting.',
    memory: '"I know you did this for us. I know you thought you had no choice. But Sophia needs her father, not his money. Come home, Amir. Whatever they\'re doing to you up there — come home. We\'ll figure it out. We always do."',
    type: 'anne',
  },
];

const WHISPER_MESSAGES = [
  'The wind whispers your name. Or someone\'s name. You\'re not sure it\'s yours anymore.',
  'Inside the howling, you hear whispers — the planet speaking to you. It whispers the names of all the people who\'ve been here.',
  'It whispers all your lost memories. Or did someone else tell you that story?',
  'The snow speaks in voices you almost recognize. Almost.',
  'A woman\'s voice in the wind: "Amir." You stop walking. The voice is gone.',
  'The planet hums beneath your feet. A low, hungry sound.',
  'You hear a child crying somewhere in the snow. When you turn, there\'s nothing there.',
  'The whispers grow louder. They know things about you that you\'ve forgotten.',
  'For a moment, the wind sounds like Anne saying your name.',
  'The planet breathes. You can feel it in your chest.',
  'A child\'s laughter, carried on the wind. Then silence.',
  'The snow hums a lullaby. Your lullaby. How does it know?',
];

const DECAY_MESSAGES = [
  'Something slips away from you. A name. A face. You can\'t remember what you\'ve lost.',
  'Your mind empties out a little more. The shell grows thicker.',
  'A memory dissolves like snow in your hands. You can\'t hold onto it.',
  'You forget something important. You can\'t remember what it was. That\'s the worst part.',
  'The planet takes another piece of you. You barely notice anymore.',
  'Your thoughts grow quieter. The silence inside you expands.',
  'You try to remember Anne\'s face. The details blur. Her eyes. What color were her eyes?',
  'A piece of your past crumbles away. You feel lighter. Emptier.',
  'The chemical aftertaste of the broth coats your tongue. Your thoughts slow.',
  'You can\'t remember why you\'re walking. You keep walking anyway.',
  'Sophia. The name means something. You repeat it. Sophia. Sophia. Don\'t forget.',
  'Your daughter\'s face flickers like a candle in the wind. Hold on. Hold on.',
];

const SEBASTIAN_MESSAGES = [
  '"Keep moving, Amir. We\'re almost there."',
  '"Tell me what you see."',
  '"You\'re doing very well. I\'m impressed with you."',
  '"The routine keeps us alive. The most important thing is to be predictable."',
  '"You\'re safe with me. You\'ll be fine."',
  '"It\'s not much farther. We need to keep walking."',
  '"How did you sleep? Any nightmares?"',
  '"Ethercorp sent new coordinates. We need to leave soon."',
];

const SEBASTIAN_DAY_MESSAGES = [
  // Day 1 — reassuring
  [
    '"Good morning, Amir. Did you sleep well?"',
    '"The planet is beautiful, isn\'t it? In its own way."',
    '"We\'re making excellent progress. Dr. Lambert will be pleased."',
    '"Tell me — what do you remember about home?"',
  ],
  // Day 2 — clinical
  [
    '"You seem disoriented today. That\'s normal. Drink your broth."',
    '"Tell me what you see when you close your eyes."',
    '"The other subjects — I mean, the other members of the team — they struggled too."',
    '"Your neural readings are... interesting. Keep walking."',
  ],
  // Day 3 — cold
  [
    '"Last day, Amir. One way or another."',
    '"You\'ve been a very good subject. Better than Caleb."',
    '"Dr. Lambert is preparing the extraction equipment."',
    '"Don\'t look at me like that. You signed the contract."',
  ],
];

const BODY_DISCOVERIES = [
  'Protruding from the snow are eight blood-caked fingers. You brush the snow away. A headless corpse. Female. Stripped of her clothes. Skin preserved in ice.',
  'A frozen hand reaches up from the snow, fingers curled as if grasping at the sky.',
  'Boot prints in the snow end abruptly. A dark stain beneath the white.',
  'A backpack, half-buried. Inside: empty pill bottles and a photograph of someone you don\'t recognize.',
  'Scratched into a rock: a name and a date. The date is months ago.',
  'A pile of clothes, neatly folded in the snow. No body. No footprints leading away.',
  'A headless torso, frozen mid-crawl. The cut is clean. Surgical.',
  'Seven graves in a row. Small stones mark each one. The eighth grave is empty. Waiting.',
];

const AMBIENT_MESSAGES = [
  'The wind howls across the plateau. Red — no, white. Everything is white here.',
  'Your boots crunch on frozen ground. Each step echoes across the emptiness.',
  'The sky is the color of nothing. Blank and infinite.',
  'Your legs hurt so much you can barely walk. You think of Anne and force yourself forward.',
  'The snow drags at your legs. Each step is a battle against this planet\'s embrace.',
  'You stop to rest. Your breath crystallizes in the air and vanishes.',
  'You count your steps. One hundred. Two hundred. Three hundred.',
  'How long have you been walking? Minutes or hours? You can\'t tell anymore.',
  'The temperature drops. Your thermal jacket compensates. Your bones don\'t.',
  'You check your hands. Still there. Still yours. For now.',
];

const NIGHTMARE_MESSAGES = [
  'You were in a lab. People in white coats watched you through a wall of glass, talking about you, studying you.',
  'You dreamed of floating above your body, slowly rising into the sky. You had no control.',
  'Angels in the snow. Whiter than white. They wanted to take you with them.',
  'A man with a reciprocating saw. Blood on the snow. You woke up screaming.',
  'You dreamed you were a brain floating in a jar of blue liquid.',
  'Dr. Lambert\'s face, smiling. "Death is not a certainty, only another step forward."',
  'Sophia was calling for you. You ran toward her voice but the snow swallowed you.',
  'Anne was standing at the window. She turned around. She had no face.',
];

// ============================================
// MAP GENERATION
// ============================================

function generateMap(day) {
  const grid = Array.from({ length: GRID_HEIGHT }, () =>
    Array.from({ length: GRID_WIDTH }, () => {
      const r = Math.random();
      if (r < 0.5) return TILE.SNOW;
      if (r < 0.75) return TILE.DEEP_SNOW;
      if (r < 0.85) return TILE.DRIFT;
      return TILE.EMPTY;
    })
  );

  for (let x = 0; x < GRID_WIDTH; x++) {
    grid[0][x] = TILE.ROCK;
    grid[GRID_HEIGHT - 1][x] = TILE.ROCK;
  }
  for (let y = 0; y < GRID_HEIGHT; y++) {
    grid[y][0] = TILE.ROCK;
    grid[y][GRID_WIDTH - 1] = TILE.ROCK;
  }

  const numRockClusters = 5 + Math.floor(Math.random() * 4) + day;
  for (let i = 0; i < numRockClusters; i++) {
    const cx = 3 + Math.floor(Math.random() * (GRID_WIDTH - 6));
    const cy = 3 + Math.floor(Math.random() * (GRID_HEIGHT - 6));
    const size = 2 + Math.floor(Math.random() * 3);

    for (let dy = -size; dy <= size; dy++) {
      for (let dx = -size; dx <= size; dx++) {
        const dist = Math.abs(dx) + Math.abs(dy);
        if (dist <= size && Math.random() < 0.6) {
          const rx = cx + dx;
          const ry = cy + dy;
          if (rx > 0 && rx < GRID_WIDTH - 1 && ry > 0 && ry < GRID_HEIGHT - 1) {
            grid[ry][rx] = TILE.ROCK;
          }
        }
      }
    }
  }

  const hazards = [];
  const numHazards = MAX_HAZARDS + (day - 1) * 2;
  for (let i = 0; i < numHazards; i++) {
    let hx, hy, attempts = 0;
    do {
      hx = 2 + Math.floor(Math.random() * (GRID_WIDTH - 4));
      hy = 2 + Math.floor(Math.random() * (GRID_HEIGHT - 4));
      attempts++;
    } while (grid[hy][hx] === TILE.ROCK && attempts < 50);

    if (grid[hy][hx] !== TILE.ROCK) {
      grid[hy][hx] = TILE.HAZARD;
      hazards.push({ x: hx, y: hy });
    }
  }

  const getEmptyTile = (minDistFrom = null, minDist = 0) => {
    let attempts = 0;
    while (attempts < 300) {
      const x = 2 + Math.floor(Math.random() * (GRID_WIDTH - 4));
      const y = 2 + Math.floor(Math.random() * (GRID_HEIGHT - 4));
      if (grid[y][x] !== TILE.ROCK && grid[y][x] !== TILE.HAZARD) {
        if (minDistFrom) {
          const dist = Math.abs(x - minDistFrom.x) + Math.abs(y - minDistFrom.y);
          if (dist < minDist) { attempts++; continue; }
        }
        return { x, y };
      }
      attempts++;
    }
    return { x: 3, y: 3 };
  };

  const campPos = { x: 4, y: Math.floor(GRID_HEIGHT / 2) };
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 2; dx++) {
      const cx = campPos.x + dx;
      const cy = campPos.y + dy;
      if (cx > 0 && cx < GRID_WIDTH - 1 && cy > 0 && cy < GRID_HEIGHT - 1) {
        grid[cy][cx] = TILE.SNOW;
      }
    }
  }
  grid[campPos.y][campPos.x] = TILE.TENT;
  grid[campPos.y][campPos.x + 1] = TILE.TENT;

  const playerStart = { x: campPos.x + 2, y: campPos.y };
  const sebastianStart = { x: campPos.x + 3, y: campPos.y };

  const fragments = [];
  const availableFragments = [...MEMORY_FRAGMENTS_DATA].sort(() => Math.random() - 0.5);
  const numFragments = Math.min(MAX_MEMORY_FRAGMENTS, availableFragments.length);

  for (let i = 0; i < numFragments; i++) {
    const pos = getEmptyTile(playerStart, 5);
    fragments.push({
      x: pos.x,
      y: pos.y,
      data: availableFragments[i],
      collected: false,
    });
  }

  const bodies = [];
  const numBodies = 2 + day;
  for (let i = 0; i < numBodies; i++) {
    const pos = getEmptyTile(playerStart, 8);
    bodies.push({
      x: pos.x,
      y: pos.y,
      discovered: false,
      message: BODY_DISCOVERIES[Math.floor(Math.random() * BODY_DISCOVERIES.length)],
    });
  }

  const campReturn = getEmptyTile(playerStart, 15);

  return {
    grid,
    playerStart,
    sebastianStart,
    campPos,
    fragments,
    bodies,
    hazards,
    campReturn,
  };
}

// ============================================
// PATHFINDING
// ============================================

function getPath(grid, from, to, maxSteps = 25) {
  const key = (x, y) => `${x},${y}`;
  const open = [{ x: from.x, y: from.y, g: 0, h: 0, parent: null }];
  const closed = new Set();
  const heuristic = (x, y) => Math.abs(x - to.x) + Math.abs(y - to.y);
  let iterations = 0;

  while (open.length > 0 && iterations < 400) {
    iterations++;
    open.sort((a, b) => (a.g + a.h) - (b.g + b.h));
    const current = open.shift();

    if (current.x === to.x && current.y === to.y) {
      const path = [];
      let node = current;
      while (node.parent) {
        path.unshift({ x: node.x, y: node.y });
        node = node.parent;
      }
      return path;
    }

    closed.add(key(current.x, current.y));

    for (const { dx, dy } of [{ dx: 0, dy: -1 }, { dx: 0, dy: 1 }, { dx: -1, dy: 0 }, { dx: 1, dy: 0 }]) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      if (nx < 0 || nx >= GRID_WIDTH || ny < 0 || ny >= GRID_HEIGHT) continue;
      if (closed.has(key(nx, ny))) continue;
      if (grid[ny][nx] === TILE.ROCK) continue;

      const g = current.g + 1;
      if (g > maxSteps) continue;

      const existing = open.find(n => n.x === nx && n.y === ny);
      if (existing) {
        if (g < existing.g) { existing.g = g; existing.parent = current; }
      } else {
        open.push({ x: nx, y: ny, g, h: heuristic(nx, ny), parent: current });
      }
    }
  }
  return [];
}

// ============================================
// HELPERS
// ============================================

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeBar(current, max, width) {
  const clamped = Math.max(0, Math.min(current, max));
  const filled = Math.round((clamped / max) * width);
  return '█'.repeat(filled) + '░'.repeat(width - filled);
}

// ============================================
// INITIAL STATE
// ============================================

function createGameState() {
  const mapData = generateMap(1);

  return {
    phase: 'intro',
    grid: mapData.grid,
    player: { ...mapData.playerStart },
    sebastian: { ...mapData.sebastianStart },
    sebastianFollowing: true,
    fragments: mapData.fragments,
    bodies: mapData.bodies,
    campReturn: mapData.campReturn,
    campPos: mapData.campPos,
    revealed: new Set(),
    footprints: new Set(),
    fragmentsCollected: 0,
    totalFragments: mapData.fragments.length,
    collectedMemories: [],
    moveCount: 0,
    clarity: 100,
    maxClarity: 100,
    day: 1,
    totalDays: TOTAL_DAYS,
    daysRemaining: TOTAL_DAYS,
    nightmareCount: 0,
    bodiesFound: 0,
    sebastianTrust: 50,
    messages: [],
    gameOver: false,
    escaped: false,
    truthRevealed: false,
    dayReturned: false,
  };
}

// ============================================
// COMPONENT
// ============================================

export default function VixiaPrime() {
  const [gs, setGs] = useState(createGameState);
  const gameRef = useRef(null);
  const gsRef = useRef(gs);

  useEffect(() => { gsRef.current = gs; }, [gs]);

  useEffect(() => {
    if (gs.phase === 'playing') gameRef.current?.focus();
  }, [gs.phase]);

  const revealAround = useCallback((px, py, revealed) => {
    const newRevealed = new Set(revealed);
    for (let dy = -FOG_RADIUS; dy <= FOG_RADIUS; dy++) {
      for (let dx = -FOG_RADIUS; dx <= FOG_RADIUS; dx++) {
        if (Math.abs(dx) + Math.abs(dy) <= FOG_RADIUS) {
          const rx = px + dx;
          const ry = py + dy;
          if (rx >= 0 && rx < GRID_WIDTH && ry >= 0 && ry < GRID_HEIGHT) {
            newRevealed.add(`${rx},${ry}`);
          }
        }
      }
    }
    return newRevealed;
  }, []);

  const startGame = useCallback(() => {
    const newState = createGameState();
    const revealed = revealAround(newState.player.x, newState.player.y, new Set());

    setGs({
      ...newState,
      phase: 'playing',
      revealed,
      messages: [
        { text: 'You open your eyes. White sky. White snow. The taste of chemicals.', type: 'story', id: Date.now() },
        { text: '"Good morning, Amir," Sebastian says. "Did you sleep well?"', type: 'sebastian', id: Date.now() + 1 },
        { text: `Find memory fragments (♦) to remember who you are. ${FRAGMENTS_TO_REMEMBER} needed.`, type: 'system', id: Date.now() + 2 },
        { text: 'Return to camp (⌂) when ready to end the day.', type: 'system', id: Date.now() + 3 },
      ],
    });
  }, [revealAround]);

  const nextDay = useCallback(() => {
    const prev = gsRef.current;
    const newMapData = generateMap(prev.day + 1);
    const revealed = revealAround(newMapData.playerStart.x, newMapData.playerStart.y, new Set());

    const nightmare = pickRandom(NIGHTMARE_MESSAGES);
    const newDay = prev.day + 1;
    const daysRemaining = prev.totalDays - newDay + 1;

    const decayAmount = 8 + (prev.day * 4);

    setGs({
      ...prev,
      grid: newMapData.grid,
      player: { ...newMapData.playerStart },
      sebastian: { ...newMapData.sebastianStart },
      fragments: newMapData.fragments,
      bodies: newMapData.bodies,
      campReturn: newMapData.campReturn,
      campPos: newMapData.campPos,
      revealed,
      footprints: new Set(),
      moveCount: 0,
      day: newDay,
      daysRemaining,
      clarity: Math.max(10, prev.clarity - decayAmount),
      nightmareCount: prev.nightmareCount + 1,
      phase: 'playing',
      dayReturned: false,
      messages: [
        { text: '═══════════════════════════════════', type: 'system', id: Date.now() },
        { text: `DAY ${newDay} OF ${TOTAL_DAYS} — ${daysRemaining === 1 ? 'FINAL DAY' : daysRemaining + ' DAYS REMAINING'}`, type: 'system', id: Date.now() + 0.1 },
        { text: '═══════════════════════════════════', type: 'system', id: Date.now() + 0.2 },
        { text: '', type: 'spacer', id: Date.now() + 0.25 },
        { text: 'You wake from another nightmare.', type: 'story', id: Date.now() + 0.3 },
        { text: `NIGHTMARE: ${nightmare}`, type: 'danger', id: Date.now() + 0.4 },
        { text: `The planet took more of you while you slept. (-${decayAmount} clarity)`, type: 'decay', id: Date.now() + 0.5 },
        { text: '', type: 'spacer', id: Date.now() + 0.55 },
        { text: pickRandom(SEBASTIAN_DAY_MESSAGES[Math.min(newDay - 1, 2)]), type: 'sebastian', id: Date.now() + 0.6 },
      ],
    });
  }, [revealAround]);

  const moveSebastian = useCallback((state) => {
    const dist = Math.abs(state.sebastian.x - state.player.x) +
                 Math.abs(state.sebastian.y - state.player.y);

    if (dist <= 2) return state.sebastian;
    if (dist > 12) {
      const path = getPath(state.grid, state.sebastian, state.player, 20);
      if (path.length > 3) {
        return { x: path[path.length - 4].x, y: path[path.length - 4].y };
      }
    }

    const path = getPath(state.grid, state.sebastian, state.player, 15);
    if (path.length > 2) {
      return { x: path[0].x, y: path[0].y };
    }

    return state.sebastian;
  }, []);

  // ── Final ending — always ends with Sebastian and the saw ──
  function handleFinalEnding(state) {
    const truthCount = state.collectedMemories.filter(m => m.type === 'truth').length;
    const anneCount = state.collectedMemories.filter(m => m.type === 'anne').length;
    const sophiaCount = state.collectedMemories.filter(m => m.type === 'sophia').length;
    const hasAnne = anneCount > 0;
    const hasSophia = sophiaCount > 0;
    const knowsTruth = truthCount >= 2;
    const remembersFamily = hasAnne && hasSophia;
    const remembersEnough = state.fragmentsCollected >= FRAGMENTS_TO_REMEMBER;

    let endingMessages = [];

    endingMessages.push({ text: '', type: 'spacer', id: Date.now() + 3 });
    endingMessages.push({ text: '═══════════════════════════════════════', type: 'ending', id: Date.now() + 3.05 });
    endingMessages.push({ text: '', type: 'spacer', id: Date.now() + 3.1 });
    endingMessages.push({ text: 'THE THIRD NIGHT', type: 'ending', id: Date.now() + 3.15 });
    endingMessages.push({ text: '', type: 'spacer', id: Date.now() + 3.2 });

    // The approach — Sebastian reveals himself
    endingMessages.push(
      { text: 'You crawl into your tent. You\'re so tired.', type: 'story', id: Date.now() + 3.3 },
      { text: '', type: 'spacer', id: Date.now() + 3.35 },
      { text: 'Sebastian unzips the tent flap. He\'s holding something behind his back.', type: 'story', id: Date.now() + 3.4 },
      { text: '"It\'s time, Amir," he says. His voice is different now. Clinical. Cold.', type: 'sebastian', id: Date.now() + 3.5 },
      { text: '', type: 'spacer', id: Date.now() + 3.55 },
      { text: 'He brings the reciprocating saw forward. Medical grade. Clean.', type: 'story', id: Date.now() + 3.6 },
      { text: '"Dr. Lambert needs the sample. You understand."', type: 'sebastian', id: Date.now() + 3.7 },
      { text: '', type: 'spacer', id: Date.now() + 3.75 },
    );

    // What Amir remembers in his final moments depends on what you found
    if (remembersFamily && knowsTruth) {
      // Best ending — remembers everything
      endingMessages.push(
        { text: 'You remember everything.', type: 'memory', id: Date.now() + 4.0 },
        { text: '', type: 'spacer', id: Date.now() + 4.05 },
        { text: 'Anne. Her black hair. Her hazel eyes. The way she kissed you in the kitchen.', type: 'memory-text', id: Date.now() + 4.1 },
        { text: 'Sophia. Her birthday candles. Her drawing of the rocket ship. DADY COMES HOME.', type: 'memory-text', id: Date.now() + 4.2 },
        { text: 'The contract you signed. The experiment. Subject 8. All of it.', type: 'memory-text', id: Date.now() + 4.3 },
        { text: '', type: 'spacer', id: Date.now() + 4.35 },
        { text: '"I know what you are," you tell Sebastian. "I know what this is."', type: 'story', id: Date.now() + 4.4 },
        { text: '', type: 'spacer', id: Date.now() + 4.45 },
        { text: 'Sebastian pauses. Something flickers across his face. Surprise, maybe.', type: 'story', id: Date.now() + 4.5 },
        { text: '"That\'s... unusual," he says. "Most subjects don\'t remember anything by now."', type: 'sebastian', id: Date.now() + 4.6 },
        { text: '', type: 'spacer', id: Date.now() + 4.65 },
        { text: 'You close your eyes. You see Anne at the window, lighting a candle.', type: 'ending', id: Date.now() + 4.7 },
        { text: 'You see Sophia drawing rocket ships at the kitchen table.', type: 'ending', id: Date.now() + 4.8 },
        { text: 'You hold onto them. You hold on as tight as you can.', type: 'ending', id: Date.now() + 4.9 },
        { text: '', type: 'spacer', id: Date.now() + 4.95 },
        { text: 'The saw starts.', type: 'danger', id: Date.now() + 5.0 },
        { text: '', type: 'spacer', id: Date.now() + 5.05 },
        { text: 'You are thinking of your wife and daughter when it ends.', type: 'ending', id: Date.now() + 5.1 },
        { text: 'You are thinking of love.', type: 'ending', id: Date.now() + 5.2 },
        { text: 'You are still yourself.', type: 'ending', id: Date.now() + 5.3 },
      );
    } else if (remembersFamily) {
      // Remembers love but not the truth
      endingMessages.push(
        { text: 'You don\'t understand what\'s happening. But you remember them.', type: 'story', id: Date.now() + 4.0 },
        { text: '', type: 'spacer', id: Date.now() + 4.05 },
        { text: 'Anne. Your wife. Her smile. Her voice on the recording.', type: 'memory-text', id: Date.now() + 4.1 },
        { text: 'Sophia. Your daughter. Three candles on a lopsided cake.', type: 'memory-text', id: Date.now() + 4.2 },
        { text: '', type: 'spacer', id: Date.now() + 4.25 },
        { text: '"Why?" you ask Sebastian. You don\'t understand the question.', type: 'story', id: Date.now() + 4.3 },
        { text: '"Because the planet showed us what\'s inside you," he says. "And Dr. Lambert wants to see it up close."', type: 'sebastian', id: Date.now() + 4.4 },
        { text: '', type: 'spacer', id: Date.now() + 4.45 },
        { text: 'You close your eyes. You think of Anne lighting a candle at the window.', type: 'ending', id: Date.now() + 4.5 },
        { text: 'You think of Sophia\'s drawing. The rocket ship that brings daddy home.', type: 'ending', id: Date.now() + 4.6 },
        { text: '', type: 'spacer', id: Date.now() + 4.65 },
        { text: 'The saw starts.', type: 'danger', id: Date.now() + 4.7 },
        { text: '', type: 'spacer', id: Date.now() + 4.75 },
        { text: 'Your last thought is of them. Their faces. Clear as daylight.', type: 'ending', id: Date.now() + 4.8 },
        { text: 'You didn\'t understand. But you remembered. That\'s something.', type: 'ending', id: Date.now() + 4.9 },
      );
    } else if (knowsTruth) {
      // Knows the truth but forgot love
      endingMessages.push(
        { text: 'You know what\'s happening. You know exactly what this is.', type: 'story', id: Date.now() + 4.0 },
        { text: '', type: 'spacer', id: Date.now() + 4.05 },
        { text: '"I\'m Subject 8," you say. "Caleb was Subject 7."', type: 'story', id: Date.now() + 4.1 },
        { text: '"Very good," Sebastian says. He sounds almost impressed.', type: 'sebastian', id: Date.now() + 4.2 },
        { text: '', type: 'spacer', id: Date.now() + 4.25 },
        { text: 'You try to remember why it matters. There was someone. A woman.', type: 'story', id: Date.now() + 4.3 },
        { text: 'A child, maybe. You can\'t remember their faces.', type: 'story', id: Date.now() + 4.4 },
        { text: 'You can\'t remember why you should be afraid.', type: 'dim', id: Date.now() + 4.5 },
        { text: '', type: 'spacer', id: Date.now() + 4.55 },
        { text: 'The saw starts.', type: 'danger', id: Date.now() + 4.6 },
        { text: '', type: 'spacer', id: Date.now() + 4.65 },
        { text: 'You know the truth. But the truth is cold.', type: 'ending', id: Date.now() + 4.7 },
        { text: 'You die knowing what happened to you.', type: 'ending', id: Date.now() + 4.8 },
        { text: 'You die not knowing who you\'re leaving behind.', type: 'dim', id: Date.now() + 4.9 },
      );
    } else if (hasAnne || hasSophia) {
      // Partial memories
      endingMessages.push(
        { text: 'Fragments. That\'s all you have left. Pieces of a life.', type: 'story', id: Date.now() + 4.0 },
        { text: '', type: 'spacer', id: Date.now() + 4.05 },
      );
      if (hasAnne) {
        endingMessages.push(
          { text: 'A woman\'s face. Black hair. You think you loved her.', type: 'memory-text', id: Date.now() + 4.1 },
        );
      }
      if (hasSophia) {
        endingMessages.push(
          { text: 'A child\'s name. Sophia. It means something. You can\'t remember what.', type: 'memory-text', id: Date.now() + 4.2 },
        );
      }
      endingMessages.push(
        { text: '', type: 'spacer', id: Date.now() + 4.25 },
        { text: '"It\'s alright," Sebastian says. "You won\'t feel anything."', type: 'sebastian', id: Date.now() + 4.3 },
        { text: '', type: 'spacer', id: Date.now() + 4.35 },
        { text: 'The saw starts.', type: 'danger', id: Date.now() + 4.4 },
        { text: '', type: 'spacer', id: Date.now() + 4.45 },
        { text: 'He\'s right. You barely feel anything anymore.', type: 'dim', id: Date.now() + 4.5 },
      );
    } else {
      // Empty — remembered nothing
      endingMessages.push(
        { text: 'You stare at Sebastian. You don\'t understand what\'s happening.', type: 'story', id: Date.now() + 4.0 },
        { text: 'You don\'t remember who you are. You don\'t remember anyone.', type: 'story', id: Date.now() + 4.1 },
        { text: '', type: 'spacer', id: Date.now() + 4.15 },
        { text: '"Don\'t worry," Sebastian says. "You\'re already gone."', type: 'sebastian', id: Date.now() + 4.2 },
        { text: '', type: 'spacer', id: Date.now() + 4.25 },
        { text: 'The saw starts.', type: 'danger', id: Date.now() + 4.3 },
        { text: '', type: 'spacer', id: Date.now() + 4.35 },
        { text: 'You feel nothing.', type: 'dim', id: Date.now() + 4.4 },
        { text: 'You are nothing.', type: 'dim', id: Date.now() + 4.5 },
      );
    }

    // Final scene — always the same
    endingMessages.push(
      { text: '', type: 'spacer', id: Date.now() + 6.0 },
      { text: '═══════════════════════════════════════', type: 'danger', id: Date.now() + 6.1 },
      { text: '', type: 'spacer', id: Date.now() + 6.15 },
      { text: 'Dr. Lambert sets a jar down on his desk.', type: 'dim', id: Date.now() + 6.2 },
      { text: 'Inside the jar, Amir\'s brain floats in blue-tinted liquid.', type: 'dim', id: Date.now() + 6.3 },
      { text: '', type: 'spacer', id: Date.now() + 6.35 },
      { text: '"Remarkable neural preservation," Lambert says, turning the jar in the light.', type: 'dim', id: Date.now() + 6.4 },
      { text: `"${remembersEnough ? 'This one held onto quite a lot. Fascinating.' : 'Empty, like the others. Prepare Subject 9.'}"`, type: 'dim', id: Date.now() + 6.5 },
      { text: '', type: 'spacer', id: Date.now() + 6.55 },
    );

    // Epilogue based on memories
    if (remembersFamily) {
      endingMessages.push(
        { text: 'On Earth, Anne puts Sophia to bed.', type: 'ending', id: Date.now() + 7.0 },
        { text: '"Is daddy coming home?" Sophia asks.', type: 'ending', id: Date.now() + 7.1 },
        { text: 'Anne turns off the light. "Soon," she says.', type: 'ending', id: Date.now() + 7.2 },
        { text: '', type: 'spacer', id: Date.now() + 7.25 },
        { text: 'She lights a candle and places it on the windowsill.', type: 'ending', id: Date.now() + 7.3 },
        { text: 'She waits.', type: 'ending', id: Date.now() + 7.4 },
      );
    }

    // Stats
    endingMessages.push(
      { text: '', type: 'spacer', id: Date.now() + 8 },
      { text: '═══════════════════════════════════════', type: 'ending', id: Date.now() + 8.1 },
      { text: `DAYS SURVIVED: ${state.day}/${TOTAL_DAYS}`, type: 'dim', id: Date.now() + 8.2 },
      { text: `MEMORIES RECOVERED: ${state.fragmentsCollected}`, type: 'dim', id: Date.now() + 8.3 },
      { text: `BODIES FOUND: ${state.bodiesFound}`, type: 'dim', id: Date.now() + 8.4 },
      { text: `CLARITY AT END: ${state.clarity}%`, type: 'dim', id: Date.now() + 8.5 },
      { text: `TRUTH DISCOVERED: ${knowsTruth ? 'YES' : 'NO'}`, type: 'dim', id: Date.now() + 8.6 },
      { text: `REMEMBERED ANNE: ${hasAnne ? 'YES' : 'NO'}`, type: 'dim', id: Date.now() + 8.7 },
      { text: `REMEMBERED SOPHIA: ${hasSophia ? 'YES' : 'NO'}`, type: 'dim', id: Date.now() + 8.8 },
      { text: '', type: 'spacer', id: Date.now() + 8.85 },
    );

    if (state.collectedMemories.length > 0) {
      endingMessages.push(
        { text: '── WHAT AMIR HELD ONTO ──', type: 'memory', id: Date.now() + 8.9 },
      );
      state.collectedMemories.forEach((m, i) => {
        endingMessages.push(
          { text: `  ♦ ${m.name}`, type: 'memory', id: Date.now() + 9.0 + i * 0.1 },
        );
      });
    } else {
      endingMessages.push(
        { text: '── AMIR HELD ONTO NOTHING ──', type: 'dim', id: Date.now() + 8.9 },
      );
    }

    endingMessages.push(
      { text: '', type: 'spacer', id: Date.now() + 10 },
      { text: '═══════════════════════════════════════', type: 'ending', id: Date.now() + 10.1 },
    );

    return {
      ...state,
      phase: 'ending',
      messages: [...state.messages.slice(-2), ...endingMessages],
      gameOver: true,
    };
  }

  // ── Clarity death ──
  function handleClarityDeath(state) {
    return {
      ...state,
      phase: 'shell',
      clarity: 0,
      messages: [...state.messages.slice(-4),
        { text: '', type: 'spacer', id: Date.now() + 2 },
        { text: '██████████████████████████████████████', type: 'danger', id: Date.now() + 2.1 },
        { text: '', type: 'spacer', id: Date.now() + 2.15 },
        { text: 'Your mind empties out completely.', type: 'death', id: Date.now() + 2.2 },
        { text: '', type: 'spacer', id: Date.now() + 2.25 },
        { text: 'You can\'t remember your name. You can\'t remember why you\'re here.', type: 'death', id: Date.now() + 2.3 },
        { text: 'You can\'t remember Anne. You can\'t remember Sophia.', type: 'death', id: Date.now() + 2.4 },
        { text: '', type: 'spacer', id: Date.now() + 2.45 },
        { text: 'You are a shell now. Empty. The planet has eaten everything.', type: 'death', id: Date.now() + 2.5 },
        { text: '', type: 'spacer', id: Date.now() + 2.55 },
        { text: 'Sebastian kneels beside you. He checks your eyes with a penlight.', type: 'sebastian', id: Date.now() + 2.6 },
        { text: '"This one\'s done," he says into the radio. "Send the saw."', type: 'sebastian', id: Date.now() + 2.7 },
        { text: '', type: 'spacer', id: Date.now() + 2.75 },
        { text: 'You don\'t feel the blade. You don\'t feel anything.', type: 'death', id: Date.now() + 2.8 },
        { text: '', type: 'spacer', id: Date.now() + 2.85 },
        { text: 'Dr. Lambert sets a jar down on his desk.', type: 'dim', id: Date.now() + 2.9 },
        { text: '"Empty. Like the others. Prepare Subject 9."', type: 'dim', id: Date.now() + 2.95 },
        { text: '', type: 'spacer', id: Date.now() + 3.0 },
        { text: '██████████████████████████████████████', type: 'danger', id: Date.now() + 3.05 },
      ],
      gameOver: true,
    };
  }

  // ── Handle move ──
  const handleMove = useCallback((dx, dy) => {
    setGs(prev => {
      if (prev.phase !== 'playing') return prev;

      const newX = prev.player.x + dx;
      const newY = prev.player.y + dy;

      if (newX < 0 || newX >= GRID_WIDTH || newY < 0 || newY >= GRID_HEIGHT) return prev;
      if (prev.grid[newY][newX] === TILE.ROCK) return prev;

      let state = { ...prev };
      state.player = { x: newX, y: newY };
      state.moveCount = prev.moveCount + 1;
      state.revealed = revealAround(newX, newY, prev.revealed);
      state.footprints = new Set(prev.footprints);
      state.footprints.add(`${prev.player.x},${prev.player.y}`);
      state.messages = [...prev.messages];

      if (prev.grid[newY][newX] === TILE.DEEP_SNOW || prev.grid[newY][newX] === TILE.DRIFT) {
        if (Math.random() < 0.3) {
          state.messages = [...state.messages.slice(-8), {
            text: 'The snow drags at your legs. Each step is a battle.',
            type: 'dim',
            id: Date.now(),
          }];
        }
      }

      // Hazard
      if (prev.grid[newY][newX] === TILE.HAZARD) {
        const dmg = 6 + Math.floor(Math.random() * 10);
        state.clarity = Math.max(0, state.clarity - dmg);
        const hazardMsgs = [
          'The ground gives way beneath you. A crevasse. You barely catch yourself.',
          'Radiation pocket. Your vision blurs. Memories scatter.',
          'Toxic gas seeps up from the ice. Your thoughts cloud over.',
          'The cold cuts through your suit. Something inside you goes numb.',
        ];
        state.messages = [...state.messages.slice(-8), {
          text: `${pickRandom(hazardMsgs)} (-${dmg} clarity)`,
          type: 'danger',
          id: Date.now(),
        }];

        if (state.clarity <= 0) {
          return handleClarityDeath(state);
        }
      }

      // Fragment
      const fragIdx = prev.fragments.findIndex(
        f => !f.collected && f.x === newX && f.y === newY
      );
      if (fragIdx !== -1) {
        const frag = prev.fragments[fragIdx];
        state.fragments = prev.fragments.map((f, i) =>
          i === fragIdx ? { ...f, collected: true } : f
        );
        state.fragmentsCollected = prev.fragmentsCollected + 1;
        state.collectedMemories = [...prev.collectedMemories, frag.data];

        const clarityBoost = 6;
        state.clarity = Math.min(state.maxClarity, state.clarity + clarityBoost);

        state.messages = [...state.messages.slice(-6),
          { text: `═══ MEMORY FRAGMENT: ${frag.data.name} ═══`, type: 'memory', id: Date.now() },
          { text: frag.data.description, type: 'story', id: Date.now() + 0.1 },
          { text: frag.data.memory, type: 'memory-text', id: Date.now() + 0.2 },
          { text: `+${clarityBoost} clarity (${state.fragmentsCollected} total memories)`, type: 'success', id: Date.now() + 0.3 },
        ];

        // Truth revelation
        const truthCount = state.collectedMemories.filter(m => m.type === 'truth').length;
        if (truthCount >= 2 && !prev.truthRevealed) {
          state.truthRevealed = true;
          state.sebastianTrust = Math.max(0, state.sebastianTrust - 30);
          state.messages = [...state.messages,
            { text: '', type: 'spacer', id: Date.now() + 0.35 },
            { text: 'Something clicks in your fractured mind. The pieces fit together.', type: 'danger', id: Date.now() + 0.4 },
            { text: 'This isn\'t an expedition. It\'s an experiment. You are a test subject.', type: 'danger', id: Date.now() + 0.5 },
            { text: 'Sebastian has been lying to you. All of them have.', type: 'danger', id: Date.now() + 0.6 },
          ];
        }

        // Memory milestone messages
        if (state.fragmentsCollected === FRAGMENTS_TO_REMEMBER) {
          state.messages = [...state.messages,
            { text: '', type: 'spacer', id: Date.now() + 0.65 },
            { text: '═══ YOU REMEMBER ENOUGH TO KNOW WHO YOU ARE ═══', type: 'ending', id: Date.now() + 0.7 },
            { text: 'Whatever happens next — you are Amir. You have a wife and a daughter.', type: 'story', id: Date.now() + 0.8 },
          ];
        }
      }

      // Body
      const bodyIdx = prev.bodies.findIndex(
        b => !b.discovered && b.x === newX && b.y === newY
      );
      if (bodyIdx !== -1) {
        const body = prev.bodies[bodyIdx];
        state.bodies = prev.bodies.map((b, i) =>
          i === bodyIdx ? { ...b, discovered: true } : b
        );
        state.bodiesFound = prev.bodiesFound + 1;
        state.clarity = Math.max(0, state.clarity - 5);
        state.messages = [...state.messages.slice(-6),
          { text: body.message, type: 'danger', id: Date.now() },
          { text: `Another subject. Another body. (-5 clarity)`, type: 'dim', id: Date.now() + 0.1 },
        ];

        if (state.clarity <= 0) {
          return handleClarityDeath(state);
        }
      }

      // Camp return
      const atCamp = (newX === prev.campReturn.x && newY === prev.campReturn.y) ||
                     (prev.grid[newY][newX] === TILE.TENT);

      if (atCamp && !prev.dayReturned && state.moveCount > 10) {
        if (prev.day >= TOTAL_DAYS) {
          // Final day — trigger ending
          return handleFinalEnding(state);
        } else {
          // End of day — transition to night
          state.dayReturned = true;
          state.phase = 'night';
          state.messages = [...state.messages.slice(-4),
            { text: '', type: 'spacer', id: Date.now() },
            { text: '═══════════════════════════════════', type: 'system', id: Date.now() + 0.1 },
            { text: `DAY ${prev.day} COMPLETE`, type: 'system', id: Date.now() + 0.2 },
            { text: '═══════════════════════════════════', type: 'system', id: Date.now() + 0.3 },
            { text: '', type: 'spacer', id: Date.now() + 0.35 },
            { text: 'You return to camp. Sebastian hands you a bowl of broth.', type: 'story', id: Date.now() + 0.4 },
            { text: 'The chemical aftertaste coats your tongue. Your eyelids grow heavy.', type: 'story', id: Date.now() + 0.5 },
            { text: '', type: 'spacer', id: Date.now() + 0.55 },
            { text: `Memories recovered today: ${state.fragmentsCollected - (prev.collectedMemories.length - state.collectedMemories.length + state.collectedMemories.length)}`, type: 'dim', id: Date.now() + 0.6 },
            { text: `Clarity: ${state.clarity}%`, type: 'dim', id: Date.now() + 0.7 },
            { text: '', type: 'spacer', id: Date.now() + 0.75 },
            { text: 'Press ENTER to sleep...', type: 'system', id: Date.now() + 0.8 },
          ];
          return state;
        }
      }

      // Sebastian movement
      if (state.sebastianFollowing) {
        state.sebastian = moveSebastian(state);
      }

      // Whisper events
      if (state.moveCount > 0 && state.moveCount % WHISPER_INTERVAL === 0) {
        state.messages = [...state.messages.slice(-8), {
          text: pickRandom(WHISPER_MESSAGES),
          type: 'whisper',
          id: Date.now() + 0.5,
        }];
      }

      // Memory decay
      if (state.moveCount > 0 && state.moveCount % MEMORY_DECAY_INTERVAL === 0) {
        const decayAmount = 3 + Math.floor(prev.day * 1.5);
        state.clarity = Math.max(0, state.clarity - decayAmount);
        state.messages = [...state.messages.slice(-8), {
          text: `${pickRandom(DECAY_MESSAGES)} (-${decayAmount} clarity)`,
          type: 'decay',
          id: Date.now() + 0.5,
        }];

        if (state.clarity <= 0) {
          return handleClarityDeath(state);
        }
      }

      // Sebastian dialogue
      const sebastianDist = Math.abs(state.sebastian.x - newX) + Math.abs(state.sebastian.y - newY);
      if (sebastianDist <= 2 && Math.random() < 0.15) {
        const dayMsgs = SEBASTIAN_DAY_MESSAGES[Math.min(prev.day - 1, 2)];
        state.messages = [...state.messages.slice(-8), {
          text: pickRandom(dayMsgs),
          type: 'sebastian',
          id: Date.now() + 0.6,
        }];
      }

      // Ambient messages
      if (state.moveCount % 11 === 0 && state.moveCount > 0) {
        state.messages = [...state.messages.slice(-8), {
          text: pickRandom(AMBIENT_MESSAGES),
          type: 'dim',
          id: Date.now() + 0.7,
        }];
      }

      return state;
    });
  }, [revealAround, moveSebastian]);

  // ── Key handler ──
  const handleKeyDown = useCallback((e) => {
    const current = gsRef.current;

    if (current.phase === 'night' && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      nextDay();
      return;
    }

    if ((current.phase === 'ending' || current.phase === 'shell') && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setGs(createGameState());
      return;
    }

    const dir = DIRECTIONS[e.key];
    if (dir && current.phase === 'playing') {
      e.preventDefault();
      handleMove(dir.dx, dir.dy);
    }
  }, [handleMove, nextDay]);

  // ── Render grid ──
  const renderGrid = useCallback(() => {
    const { grid, player, sebastian, fragments, bodies, revealed, footprints, campReturn } = gs;
    const rows = [];

    for (let y = 0; y < GRID_HEIGHT; y++) {
      let row = '';
      let colors = [];

      for (let x = 0; x < GRID_WIDTH; x++) {
        const key = `${x},${y}`;
        const isRevealed = revealed.has(key);
        const dist = Math.abs(x - player.x) + Math.abs(y - player.y);

        if (!isRevealed) {
          row += ' ';
          colors.push(TILE_COLORS['fog']);
          continue;
        }

        // Player
        if (x === player.x && y === player.y) {
          row += TILE.PLAYER;
          colors.push(TILE_COLORS[TILE.PLAYER]);
          continue;
        }

        // Sebastian
        if (x === sebastian.x && y === sebastian.y) {
          row += TILE.SEBASTIAN;
          colors.push(TILE_COLORS[TILE.SEBASTIAN]);
          continue;
        }

        // Fragment
        const frag = fragments.find(f => !f.collected && f.x === x && f.y === y);
        if (frag && dist <= FOG_RADIUS) {
          row += TILE.FRAGMENT;
          colors.push(TILE_COLORS[TILE.FRAGMENT]);
          continue;
        }

        // Body
        const body = bodies.find(b => !b.discovered && b.x === x && b.y === y);
        if (body && dist <= FOG_RADIUS) {
          row += TILE.BODY;
          colors.push(TILE_COLORS[TILE.BODY]);
          continue;
        }

        // Camp return marker
        if (x === campReturn.x && y === campReturn.y) {
          row += TILE.CAMP;
          colors.push(TILE_COLORS[TILE.CAMP]);
          continue;
        }

        // Footprints
        if (footprints.has(key)) {
          row += TILE.FOOTPRINT;
          colors.push(TILE_COLORS[TILE.FOOTPRINT]);
          continue;
        }

        // Terrain
        const tile = grid[y][x];
        row += tile;

        // Dim tiles further from player
        if (dist > FOG_RADIUS - 1) {
          colors.push('#1a1a2a');
        } else {
          colors.push(TILE_COLORS[tile] || '#334455');
        }
      }

      rows.push(
        <div key={y} style={{ lineHeight: '1.2', height: '1.2em' }}>
          {row.split('').map((char, x) => (
            <span key={x} style={{ color: colors[x] }}>{char}</span>
          ))}
        </div>
      );
    }

    return rows;
  }, [gs]);

  // ── Render messages ──
  const renderMessages = useCallback(() => {
    const messageColors = {
      'story': '#c0c0c0',
      'sebastian': '#ff0040',
      'system': '#555555',
      'memory': '#ffb000',
      'memory-text': '#ffd080',
      'danger': '#ff4444',
      'death': '#ff0040',
      'success': '#00ff41',
      'whisper': '#6a5acd',
      'decay': '#8b4513',
      'dim': '#444444',
      'ending': '#00e5ff',
      'spacer': 'transparent',
    };

    return gs.messages.slice(-12).map(msg => (
      <div
        key={msg.id}
        style={{
          color: messageColors[msg.type] || '#888',
          marginBottom: msg.type === 'spacer' ? '8px' : '4px',
          fontSize: '0.85rem',
          lineHeight: '1.4',
          minHeight: msg.type === 'spacer' ? '8px' : 'auto',
        }}
      >
        {msg.text}
      </div>
    ));
  }, [gs.messages]);

  // ── INTRO SCREEN ──
  if (gs.phase === 'intro') {
    return (
      <div style={{
        maxWidth: '700px',
        margin: '0 auto',
        padding: '30px 20px',
        fontFamily: "'VT323', 'Courier New', monospace",
        color: '#c0c0c0',
        background: '#0a0a0a',
        minHeight: '100vh',
      }}>
        <div style={{ color: '#00e5ff', fontSize: '1.5rem', marginBottom: '5px' }}>
          V I X I A &nbsp; P R I M E
        </div>
        <div style={{ color: '#555', marginBottom: '25px', fontSize: '0.85rem' }}>
          A MEMORY EXPLORATION GAME
        </div>

        <div style={{ borderTop: '1px solid #333', paddingTop: '20px', marginBottom: '20px' }}>
          <p>You are <span style={{ color: '#00e5ff' }}>Amir</span>. You don't remember much else.</p>
          <p>You are on a planet called <span style={{ color: '#6a5acd' }}>Vixia Prime</span>. It is cold and white and it whispers to you.</p>
          <p>A man named <span style={{ color: '#ff0040' }}>Sebastian</span> says he is your guide. He says you are on an expedition.</p>
          <p>You don't remember signing up for an expedition.</p>
          <p style={{ marginTop: '15px' }}>
            Find <span style={{ color: '#ffb000' }}>memory fragments (♦)</span> scattered across the snow.
            Each one returns a piece of who you are.
          </p>
          <p>
            Return to <span style={{ color: '#8b00ff' }}>camp (▲)</span> or your <span style={{ color: '#556677' }}>tent (⌂)</span> to end each day.
          </p>
          <p>
            You have <span style={{ color: '#ff0040' }}>{TOTAL_DAYS} days</span>.
            Your <span style={{ color: '#00ff41' }}>clarity</span> fades with each passing hour.
          </p>
          <p>
            Avoid <span style={{ color: '#4a4a6a' }}>hazards (░)</span>. They drain what's left of your mind.
          </p>
        </div>

        <div style={{ color: '#555', marginBottom: '20px', fontSize: '0.85rem' }}>
          <div>WASD or Arrow Keys to move</div>
          <div>@ = You &nbsp; S = Sebastian &nbsp; ♦ = Memory &nbsp; × = Body</div>
          <div>▲ = Camp &nbsp; ⌂ = Tent &nbsp; ░ = Hazard &nbsp; █ = Rock</div>
        </div>

        <button
          className="bbs-button"
          onClick={startGame}
          style={{
            padding: '12px 40px',
            fontSize: '1.1rem',
            borderColor: '#00e5ff',
            color: '#00e5ff',
          }}
        >
          WAKE UP
        </button>
      </div>
    );
  }

  // ── MAIN GAME / ENDING / SHELL SCREEN ──
  return (
    <div
      ref={gameRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '10px',
        fontFamily: "'VT323', 'Courier New', monospace",
        background: '#0a0a0a',
        outline: 'none',
        minHeight: '100vh',
      }}
    >
      {/* HUD */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid #222',
        marginBottom: '10px',
        flexWrap: 'wrap',
        gap: '8px',
      }}>
        <div style={{ color: '#00e5ff', fontSize: '0.9rem' }}>
          VIXIA PRIME — DAY {gs.day}/{gs.totalDays}
        </div>
        <div style={{ display: 'flex', gap: '20px', fontSize: '0.85rem' }}>
          <span style={{ color: '#00ff41' }}>
            CLARITY: [{makeBar(gs.clarity, gs.maxClarity, 15)}] {gs.clarity}%
          </span>
          <span style={{ color: '#ffb000' }}>
            ♦ {gs.fragmentsCollected}
          </span>
        </div>
      </div>

      {/* Grid */}
      {gs.phase === 'playing' && (
        <div style={{
          background: '#000',
          padding: '10px',
          border: '1px solid #1a1a2a',
          marginBottom: '10px',
          fontSize: '14px',
          letterSpacing: '2px',
          lineHeight: '1.2',
          overflowX: 'auto',
        }}>
          {renderGrid()}
        </div>
      )}

      {/* Messages */}
      <div style={{
        padding: '15px',
        border: '1px solid #1a1a2a',
        background: '#050505',
        minHeight: '200px',
        maxHeight: '400px',
        overflowY: 'auto',
      }}>
        {renderMessages()}
      </div>

      {/* Bottom controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        marginTop: '10px',
        fontSize: '0.8rem',
        color: '#333',
      }}>
        <span>WASD / ARROWS TO MOVE</span>
        {(gs.phase === 'ending' || gs.phase === 'shell') && (
          <button
            className="bbs-button"
            onClick={() => setGs(createGameState())}
            style={{ padding: '6px 20px', fontSize: '0.85rem' }}
          >
            PLAY AGAIN
          </button>
        )}
        {gs.phase === 'night' && (
          <button
            className="bbs-button"
            onClick={nextDay}
            style={{
              padding: '6px 20px',
              fontSize: '0.85rem',
              borderColor: '#6a5acd',
              color: '#6a5acd',
            }}
          >
            SLEEP...
          </button>
        )}
      </div>
    </div>
  );
}
